<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES, TWILIO_CHANNEL_MEDIUM } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NodePanelInput from './components/ui/NodePanelInput.vue';
import NodeSelectField from './components/ui/NodeSelectField.vue';
import {
  appendInteractiveButtonBranch,
  appendInteractiveListRow,
  appendInteractiveListSection,
  canInsertAfterNode,
  cloneDefinition,
  createEmptyDefinition,
  duplicateNodeInDefinition,
  getNodeMap,
  insertBlockAfter,
  normalizeDefinition,
  reorderInteractiveButtonBranch,
  removeNodeFromDefinition,
  updateNodeInDefinition,
} from './mobileFlowBuilder';
import { buildMobileConversationFlowTemplate } from './mobileFlowTemplates';
import { validateMobileFlow } from './mobileFlowValidation';
import PhoneCanvas from './components/PhoneCanvas.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const uiCopy = {
  flowSettings: 'إعدادات التدفق',
  flowSettingsHint: 'إعدادات عامة سريعة للتدفق.',
  toolboxHintIdle: 'اختر خطوة من القائمة ثم ضعها بعد الرسالة المحددة.',
  toolboxHintEntry: 'ابدأ أولاً بإضافة نقطة دخول واحدة فقط.',
  quickAddTitle: 'إضافة سريعة',
  quickAddDescription: 'حدد رسالة ثم أضف ما بعدها مباشرة.',
  flowNamePlaceholder: 'اكتب اسم للتدفق',
  flowNameRequired: 'اسم التدفق مطلوب قبل النشر.',
};

const flow = useMapGetter('conversationFlows/getConversationFlow');
const flows = useMapGetter('conversationFlows/getConversationFlows');
const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('conversationFlows/getUIFlags');

const isNewFlow = computed(() => route.params.flowId === 'new');
const selectedTemplateId = computed(() => route.query.templateId || '');

const selectedInbox = ref(null);
const selectedNodeId = ref('');
const workingDefinition = ref(createEmptyDefinition());
const flowName = ref('');
const flowDescription = ref('');

const toolboxItems = computed(() => [
  {
    id: 'start',
    label: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.START'),
    description: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.START_DESC'),
    icon: 'i-lucide-sparkles',
  },
  {
    id: 'noMatch',
    label: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.NO_MATCH'),
    description: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.NO_MATCH_DESC'),
    icon: 'i-lucide-shield-question',
  },
  {
    id: 'text',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.TEXT'),
    description: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.TEXT_DESC'),
    icon: 'i-lucide-message-square-text',
  },
  {
    id: 'interactiveButtons',
    label: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.INTERACTIVE_BUTTONS'),
    shortLabel: 'أزرار',
    description: t(
      'MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.INTERACTIVE_BUTTONS_DESC'
    ),
    icon: 'i-lucide-messages-square',
  },
  {
    id: 'interactiveList',
    label: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.INTERACTIVE_LIST'),
    shortLabel: 'قائمة',
    description: t(
      'MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.INTERACTIVE_LIST_DESC'
    ),
    icon: 'i-lucide-list-collapse',
  },
  {
    id: 'carousel',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.CAROUSEL'),
    shortLabel: 'كاروسيل',
    description: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.CAROUSEL_DESC'),
    icon: 'i-lucide-panels-top-left',
  },
  {
    id: 'locationRequest',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.LOCATION_REQUEST'),
    shortLabel: 'موقع',
    description: t(
      'MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.LOCATION_REQUEST_DESC'
    ),
    icon: 'i-lucide-map-pinned',
  },
  {
    id: 'contact',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.CONTACT'),
    shortLabel: 'جهة اتصال',
    description: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.CONTACT_DESC'),
    icon: 'i-lucide-contact-round',
  },
  {
    id: 'ai',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.AI'),
    shortLabel: 'AI',
    description: t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.AI_DESC'),
    icon: 'i-lucide-bot-message-square',
  },
]);

const isWhatsAppInbox = inbox =>
  inbox.channel_type === INBOX_TYPES.WHATSAPP ||
  (inbox.channel_type === INBOX_TYPES.TWILIO &&
    inbox.medium === TWILIO_CHANNEL_MEDIUM.WHATSAPP);

const inboxOptions = computed(() =>
  inboxes.value
    .filter(inbox => isWhatsAppInbox(inbox))
    .map(inbox => ({
      id: inbox.id,
      name: inbox.name,
      icon: 'i-woot-whatsapp',
    }))
);

const defaultDraft = computed(() => ({
  id: 'new',
  name: '',
  description: '',
  channel_type: 'whatsapp',
  inbox_id: Number(route.query.inboxId) || null,
  status: 'draft',
  definition: selectedTemplateId.value
    ? buildMobileConversationFlowTemplate(selectedTemplateId.value, {
        startTitle: 'بداية التدفق',
        noMatchTitle: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
        cardCta: 'عرض',
      })
    : createEmptyDefinition(),
}));

const currentFlow = computed(() =>
  isNewFlow.value ? defaultDraft.value : flow.value || {}
);

const selectedNode = computed(
  () => getNodeMap(workingDefinition.value)[selectedNodeId.value] || null
);

const flowTitle = computed(
  () => flowName.value || t('MOBILE_CONVERSATION_FLOWS.EDITOR.TITLE')
);

const entryNodeCount = computed(
  () =>
    normalizeDefinition(workingDefinition.value).nodes.filter(node =>
      ['start', 'noMatch'].includes(node.type)
    ).length
);

const canInsert = computed(() => canInsertAfterNode(selectedNode.value));

const toolboxHelperText = computed(() =>
  entryNodeCount.value ? uiCopy.toolboxHintIdle : uiCopy.toolboxHintEntry
);

const quickInsertItems = computed(() =>
  toolboxItems.value.filter(item => !['start', 'noMatch'].includes(item.id))
);

const fallbackAnchorNodeId = computed(() => {
  if (selectedNode.value?.id) return selectedNode.value.id;
  const nodes = normalizeDefinition(workingDefinition.value).nodes;
  return nodes[nodes.length - 1]?.id || null;
});

const initializeState = payload => {
  flowName.value = payload?.name || '';
  flowDescription.value = payload?.description || '';
  workingDefinition.value = cloneDefinition(payload?.definition);

  const inboxId =
    payload?.inbox_id ||
    Number(route.query.inboxId) ||
    inboxOptions.value[0]?.id;

  selectedInbox.value =
    inboxOptions.value.find(option => option.id === inboxId) || null;
};

const goBack = () => {
  router.push({ name: 'mobile_conversation_flows_index' });
};

const loadFlow = async () => {
  await store.dispatch('conversationFlows/get');
  await store.dispatch('inboxes/get');

  if (!isNewFlow.value) {
    await store.dispatch('conversationFlows/show', route.params.flowId);
  }
};

const addBlock = (blockType, anchorNodeId = fallbackAnchorNodeId.value) => {
  if (
    (blockType === 'start' || blockType === 'noMatch') &&
    entryNodeCount.value > 0
  ) {
    useAlert(t('MOBILE_CONVERSATION_FLOWS.EDITOR.ENTRY_EXISTS'));
    return;
  }

  if (!entryNodeCount.value && !['start', 'noMatch'].includes(blockType)) {
    const seededDefinition = insertBlockAfter({
      definition: workingDefinition.value,
      anchorNodeId: null,
      blockType: 'start',
      labels: {
        startTitle: 'بداية التدفق',
        noMatchTitle: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
      },
    });

    const seededStart = normalizeDefinition(seededDefinition).nodes.find(
      node => node.type === 'start'
    );

    workingDefinition.value = insertBlockAfter({
      definition: seededDefinition,
      anchorNodeId: seededStart?.id || null,
      blockType,
      labels: {
        startTitle: 'بداية التدفق',
        noMatchTitle: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
      },
    });

    selectedNodeId.value = seededStart?.id || '';
    return;
  }

  const anchorNode = anchorNodeId
    ? getNodeMap(workingDefinition.value)[anchorNodeId]
    : null;

  if (anchorNode && !canInsertAfterNode(anchorNode)) {
    useAlert(t('MOBILE_CONVERSATION_FLOWS.EDITOR.INVALID_INSERT_TARGET'));
    return;
  }

  workingDefinition.value = insertBlockAfter({
    definition: workingDefinition.value,
    anchorNodeId,
    blockType,
    labels: {
      startTitle: 'بداية التدفق',
      noMatchTitle: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
    },
  });

  if (anchorNodeId) {
    selectedNodeId.value = anchorNodeId;
  }
};

const applyNodePatch = (nodeId, patch) => {
  workingDefinition.value = updateNodeInDefinition(
    workingDefinition.value,
    nodeId,
    node => ({
      ...node,
      config: {
        ...(node.config || {}),
        ...patch,
      },
      summary:
        patch.body ||
        patch.title ||
        patch.buttonText ||
        node.summary ||
        node.label,
    })
  );
};

const updateNodeById = ({ nodeId, patch }) => {
  if (!nodeId || !patch) return;
  selectedNodeId.value = nodeId;
  applyNodePatch(nodeId, patch);
};

const handleCanvasInsert = nodeId => {
  selectedNodeId.value = nodeId;
};

const handleQuickInsert = payload => {
  if (!payload) return;

  if (typeof payload === 'string') {
    addBlock(payload, fallbackAnchorNodeId.value);
    return;
  }

  addBlock(
    payload.blockType,
    payload.anchorNodeId || fallbackAnchorNodeId.value
  );
};

const deleteNodeById = nodeId => {
  if (!nodeId) return;

  workingDefinition.value = removeNodeFromDefinition(
    workingDefinition.value,
    nodeId
  );

  const remainingNodes = normalizeDefinition(workingDefinition.value).nodes;
  selectedNodeId.value = remainingNodes[0]?.id || '';
};

const duplicateNodeById = nodeId => {
  if (!nodeId) return;

  const sourceNode = getNodeMap(workingDefinition.value)[nodeId];
  if (!sourceNode || ['start', 'noMatch'].includes(sourceNode.type)) {
    return;
  }

  const result = duplicateNodeInDefinition(workingDefinition.value, nodeId);
  workingDefinition.value = result.definition;
  selectedNodeId.value = result.duplicatedNodeId || nodeId;
};

const addInteractiveButtonBranch = nodeId => {
  if (!nodeId) return;

  workingDefinition.value = appendInteractiveButtonBranch(
    workingDefinition.value,
    nodeId
  );
  selectedNodeId.value = nodeId;
};

const moveInteractiveButtonBranch = ({
  interactiveNodeId,
  buttonNodeId,
  direction,
}) => {
  if (!interactiveNodeId || !buttonNodeId || !direction) return;

  workingDefinition.value = reorderInteractiveButtonBranch(
    workingDefinition.value,
    interactiveNodeId,
    buttonNodeId,
    direction
  );
  selectedNodeId.value = interactiveNodeId;
};

const addInteractiveListRow = sectionNodeId => {
  if (!sectionNodeId) return;

  workingDefinition.value = appendInteractiveListRow(
    workingDefinition.value,
    sectionNodeId
  );
  selectedNodeId.value = sectionNodeId;
};

const addInteractiveListSection = keyboardNodeId => {
  if (!keyboardNodeId) return;

  workingDefinition.value = appendInteractiveListSection(
    workingDefinition.value,
    keyboardNodeId
  );
  selectedNodeId.value = keyboardNodeId;
};

const persistFlow = async status => {
  if (uiFlags.value.isCreating || uiFlags.value.isUpdating) {
    return;
  }

  if (!flowName.value.trim()) {
    useAlert(uiCopy.flowNameRequired);
    return;
  }

  const validationError = validateMobileFlow({
    definition: workingDefinition.value,
    flowName: flowName.value,
    selectedInboxId: selectedInbox.value?.id,
    flows: flows.value,
    currentFlowId: currentFlow.value?.id,
    t,
  });

  if (validationError) {
    useAlert(validationError);
    return;
  }

  const payload = {
    name: flowName.value,
    description: flowDescription.value,
    status,
    channel_type: 'whatsapp',
    inbox_id: selectedInbox.value?.id,
    definition: workingDefinition.value,
  };

  try {
    if (isNewFlow.value) {
      const createdFlow = await store.dispatch(
        'conversationFlows/create',
        payload
      );
      await router.replace({
        name: 'mobile_conversation_flow_editor',
        params: { flowId: createdFlow.id },
      });
      await store.dispatch('conversationFlows/show', createdFlow.id);
    } else {
      await store.dispatch('conversationFlows/update', {
        id: currentFlow.value.id,
        ...payload,
      });
      await store.dispatch('conversationFlows/show', currentFlow.value.id);
    }

    await store.dispatch('conversationFlows/get');

    useAlert(
      status === 'published'
        ? t('CONVERSATION_FLOWS.EDITOR.PUBLISH_SUCCESS')
        : t('CONVERSATION_FLOWS.EDITOR.SAVE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      status === 'published'
        ? t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERROR')
        : t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR')
    );
  }
};

watch(
  [currentFlow, inboxOptions],
  () => {
    if (!inboxOptions.value.length) return;
    initializeState(currentFlow.value);
  },
  { immediate: true }
);

watch(
  () => workingDefinition.value,
  value => {
    const firstNode = normalizeDefinition(value).nodes[0];
    if (!selectedNodeId.value && firstNode) {
      selectedNodeId.value = firstNode.id;
    }
  },
  { deep: true, immediate: true }
);

onMounted(loadFlow);
</script>

<template>
  <SettingsLayout
    :loading-message="$t('CONVERSATION_FLOWS.LOADING')"
    :show-pagination-footer="false"
  >
    <div class="flex w-full min-w-[70rem] flex-col gap-4 xl:min-w-[76rem]">
      <div
        class="flex flex-wrap items-center justify-between gap-3 rounded-[1.5rem] border border-n-slate-3 bg-white/95 px-4 py-2.5 shadow-sm transition-colors dark:border-white/10 dark:bg-[#111827]/92 dark:shadow-[0_20px_50px_rgba(2,6,23,0.45)]"
      >
        <div class="min-w-0">
          <button
            type="button"
            class="mb-1.5 inline-flex items-center gap-2 rounded-full bg-n-slate-2 px-3 py-1 text-xs text-n-slate-11 transition hover:bg-n-slate-3 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
            @click="goBack"
          >
            <i class="i-lucide-arrow-left rtl:rotate-180" />
            {{ $t('MOBILE_CONVERSATION_FLOWS.EDITOR.BACK') }}
          </button>
          <h1 class="text-lg font-semibold text-n-slate-12 dark:text-white">
            {{ flowTitle }}
          </h1>
          <p class="mt-0.5 text-xs text-n-slate-11 dark:text-slate-300">
            {{ $t('MOBILE_CONVERSATION_FLOWS.EDITOR.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div
        class="grid gap-4 xl:grid-cols-[17rem_minmax(32rem,1fr)_17rem] xl:items-start"
      >
        <aside
          class="flex flex-col gap-4 self-start rounded-[2rem] border border-n-slate-3 bg-white p-4 shadow-sm transition-colors xl:sticky xl:top-4 dark:border-white/10 dark:bg-[#111827]/90 dark:shadow-[0_18px_45px_rgba(2,6,23,0.4)]"
        >
          <div
            class="rounded-[1.5rem] border border-n-slate-3 bg-white p-4 transition-colors dark:border-white/10 dark:bg-[#0f172a]/78"
          >
            <div class="mb-3">
              <h2
                class="text-base font-semibold text-n-slate-12 dark:text-white"
              >
                {{ $t('MOBILE_CONVERSATION_FLOWS.EDITOR.TOOLBOX.TITLE') }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-11 dark:text-slate-300">
                {{ toolboxHelperText }}
              </p>
            </div>

            <div class="grid gap-2">
              <button
                v-for="item in toolboxItems"
                :key="item.id"
                type="button"
                class="group rounded-[1.25rem] border border-n-slate-3 bg-white px-3 py-3 text-start transition hover:border-[#25d366]/50 hover:bg-[#25d366]/5 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-[#111827]/65 dark:hover:bg-[#25d366]/8"
                :disabled="
                  !canInsert && item.id !== 'start' && item.id !== 'noMatch'
                "
                @click="addBlock(item.id)"
              >
                <div class="flex items-start gap-3">
                  <span
                    class="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-n-slate-2 text-n-slate-12 transition group-hover:bg-[#25d366]/10 group-hover:text-[#128c7e] dark:bg-white/5 dark:text-slate-100"
                  >
                    <i class="text-lg" :class="[item.icon]" />
                  </span>
                  <div class="min-w-0">
                    <div
                      class="text-sm font-medium text-n-slate-12 dark:text-slate-100"
                    >
                      {{ item.label }}
                    </div>
                    <div
                      class="mt-1 text-xs leading-5 text-n-slate-11 dark:text-slate-300"
                    >
                      {{ item.description }}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div
            v-if="selectedNode"
            class="rounded-[1.5rem] border border-dashed border-[#25d366]/35 bg-[#25d366]/5 p-4 transition-colors dark:bg-[#0f3b2c]/25"
          >
            <div class="mb-3">
              <h3 class="text-sm font-semibold text-[#0f6b5a]">
                {{ uiCopy.quickAddTitle }}
              </h3>
              <p class="mt-1 text-xs leading-5 text-[#0f6b5a]/80">
                {{ uiCopy.quickAddDescription }}
              </p>
            </div>

            <div class="flex flex-wrap gap-2">
              <button
                v-for="item in quickInsertItems"
                :key="`quick_${item.id}`"
                type="button"
                class="rounded-full border border-[#25d366]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#0f6b5a] transition hover:bg-[#25d366]/10 dark:bg-[#0f172a] dark:text-emerald-200"
                @click="addBlock(item.id, selectedNode.id)"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
        </aside>

        <main
          class="min-w-[32rem] rounded-[2rem] border border-n-slate-3 bg-[radial-gradient(circle_at_top,_rgba(37,211,102,0.08),_transparent_28%),linear-gradient(180deg,#ffffff_0%,#f6f8fb_100%)] p-3 shadow-sm transition-colors dark:border-white/10 dark:bg-[radial-gradient(circle_at_top,_rgba(37,211,102,0.12),_transparent_24%),linear-gradient(180deg,#0f172a_0%,#111827_100%)] dark:shadow-[0_24px_60px_rgba(2,6,23,0.45)]"
        >
          <div class="mb-2 flex items-center justify-between gap-4">
            <div>
              <h2
                class="text-base font-semibold text-n-slate-12 dark:text-white"
              >
                {{ $t('MOBILE_CONVERSATION_FLOWS.EDITOR.CANVAS_TITLE') }}
              </h2>
              <p class="mt-0.5 text-xs text-n-slate-11 dark:text-slate-300">
                {{ $t('MOBILE_CONVERSATION_FLOWS.EDITOR.CANVAS_SUBTITLE') }}
              </p>
            </div>

            <div
              v-if="selectedNode"
              class="inline-flex items-center gap-2 rounded-full border border-[#25d366]/25 bg-white px-3 py-1.5 text-xs text-[#0f6b5a] dark:bg-[#0f172a] dark:text-emerald-200"
            >
              <span class="h-2 w-2 rounded-full bg-[#25d366]" />
              {{ selectedNode.label || selectedNode.type }}
            </div>
          </div>

          <PhoneCanvas
            :definition="workingDefinition"
            :selected-node-id="selectedNodeId"
            :quick-insert-items="quickInsertItems"
            @select="selectedNodeId = $event"
            @insert-after="handleCanvasInsert"
            @update-node="updateNodeById"
            @delete-node="deleteNodeById"
            @duplicate-node="duplicateNodeById"
            @add-interactive-button="addInteractiveButtonBranch"
            @move-interactive-button="moveInteractiveButtonBranch"
            @add-interactive-list-row="addInteractiveListRow"
            @add-interactive-list-section="addInteractiveListSection"
            @quick-insert="handleQuickInsert"
          />
        </main>

        <aside
          class="self-start rounded-[1.5rem] border border-n-slate-3 bg-white p-3 shadow-sm transition-colors xl:sticky xl:top-4 dark:border-white/10 dark:bg-[#111827]/90 dark:shadow-[0_18px_45px_rgba(2,6,23,0.4)]"
        >
          <div class="mb-2">
            <h2 class="text-base font-semibold text-n-slate-12 dark:text-white">
              {{ uiCopy.flowSettings }}
            </h2>
            <p class="mt-0.5 text-xs text-n-slate-11 dark:text-slate-300">
              {{ uiCopy.flowSettingsHint }}
            </p>
          </div>

          <div class="grid gap-2.5">
            <label
              class="flex flex-col gap-1.5 text-sm text-n-slate-12 dark:text-slate-100"
            >
              <span>{{ $t('CONVERSATION_FLOWS.EDITOR.CHANNEL_SELECT') }}</span>
              <NodeSelectField
                v-model="selectedInbox"
                :options="inboxOptions"
                :placeholder="
                  $t('CONVERSATION_FLOWS.EDITOR.CHANNEL_PLACEHOLDER')
                "
                :search-placeholder="
                  $t('CONVERSATION_FLOWS.EDITOR.CHANNEL_SEARCH')
                "
              />
            </label>

            <label
              class="flex flex-col gap-1.5 text-sm text-n-slate-12 dark:text-slate-100"
            >
              <span>{{
                $t('MOBILE_CONVERSATION_FLOWS.EDITOR.FLOW_NAME')
              }}</span>
              <NodePanelInput
                v-model="flowName"
                :placeholder="uiCopy.flowNamePlaceholder"
                variant="settings"
              />
            </label>

            <Button
              :label="$t('CONVERSATION_FLOWS.EDITOR.PUBLISH')"
              :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
              :disabled="uiFlags.isCreating || uiFlags.isUpdating"
              @click="persistFlow('published')"
            />
          </div>
        </aside>
      </div>
    </div>
  </SettingsLayout>
</template>
